import { useState, useEffect } from "react";
import { Card, Table, Spinner } from "react-bootstrap";
import { Search, X } from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { Currency } from "../../../../lib/components/Currency";
import { FormattedDate } from "../../../../lib/components/FormattedDate";
import { Pager } from "../../../../lib/components/Pager";
import { StatusBadge } from "./StatusBadge";
import { SubmitButton, CancelButton } from "../../shared";

const MONEY_COLUMNS = [
  { key: "commissions", label: "Commissions" },
  { key: "chargebacks", label: "Chargebacks", tone: "danger" },
  { key: "bonuses", label: "Bonuses" },
  { key: "adjustments", label: "Adjustments" },
  { key: "rejected", label: "Declined", tone: "danger" },
  { key: "netPayable", label: "Net payable", emphasis: true },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function RunsTable({
  runs = [],
  query = "",
  onSearch,
  page,
  pageSize,
  totalRecords,
  totalPages,
  onPagingChange,
  isLoading = false,
}) {
  const [searchInput, setSearchInput] = useState(query || "");

  useEffect(() => {
    setSearchInput(query || "");
  }, [query]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchInput);
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    if (onSearch) {
      onSearch("");
    }
  };

  return (
    <Card className="table-card">
      <div className="table-card-header">
        <form className="table-search-form" onSubmit={handleSearchSubmit}>
          <div className="table-search-input-wrap">
            <Search
              className="table-search-icon"
              size={15}
              aria-hidden="true"
            />
            <input
              type="text"
              className="table-search-input"
              placeholder="Search by run name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              disabled={isLoading}
            />
            {searchInput && !isLoading && (
              <button
                type="button"
                className="table-search-clear"
                onClick={handleClearSearch}
                title="Clear search"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <SubmitButton
            type="submit"
            text="Search"
            color="blue"
            isSubmitting={isLoading}
          />
        </form>
      </div>

      <div className="table-responsive-container">
        {isLoading && (
          <div className="table-loading-overlay">
            <div className="table-loading-content">
              <Spinner
                animation="border"
                role="status"
                className="table-spinner"
              >
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <span className="table-loading-text">Loading payout runs...</span>
            </div>
          </div>
        )}

        <Table
          hover
          responsive
          className="data-table data-table--wide data-table--interactive align-middle"
        >
          <thead>
            <tr>
              <th>Run</th>
              <th className="col-date">Start</th>
              <th className="col-date">End</th>
              {MONEY_COLUMNS.map((c) => (
                <th key={c.key} className="col-money">
                  {c.label}
                </th>
              ))}
              <th className="col-status">Status</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && !isLoading && (
              <tr>
                <td
                  colSpan={MONEY_COLUMNS.length + 4}
                  className="no-results-cell text-center"
                >
                  <div className="no-results-box">
                    <p className="mb-1 fw-medium text-secondary">
                      {query
                        ? `No payout runs match "${query}"`
                        : "No payout runs found."}
                    </p>
                    {query && (
                      <CancelButton
                        text="Clear search filter"
                        onClick={handleClearSearch}
                        className="mt-1"
                      />
                    )}
                  </div>
                </td>
              </tr>
            )}
            {runs.length === 0 && isLoading && (
              <tr>
                <td
                  colSpan={MONEY_COLUMNS.length + 4}
                  style={{ height: "180px" }}
                >
                  {/* Empty height placeholder while overlay is visible */}
                </td>
              </tr>
            )}
            {runs.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link
                    to={`/commissions-payout-runs/${r.id}`}
                    className="run-link"
                  >
                    {r.name}
                  </Link>
                </td>
                <td className="col-date">
                  <FormattedDate date={r.periodStart} format="MMM D, YYYY" />
                </td>
                <td className="col-date">
                  <FormattedDate date={r.periodEnd} format="MMM D, YYYY" />
                </td>
                {MONEY_COLUMNS.map((c) => {
                  const cls = [
                    "col-money tnum",
                    c.tone === "danger" ? "col-money--danger" : "",
                    c.emphasis ? "col-money--emphasis" : "",
                  ]
                    .join(" ")
                    .trim();
                  return (
                    <td key={c.key} className={cls}>
                      <Currency amount={r[c.key]} />
                    </td>
                  );
                })}
                <td className="col-status">
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="table-card-footer">
        <Pager
          variant="modern"
          page={page}
          pageSize={pageSize}
          maxPages={Math.max(totalPages, 1)}
          maxItems={totalRecords}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageChange={onPagingChange}
          onPageSizeChange={onPagingChange}
          isLoading={isLoading}
        />
      </div>
    </Card>
  );
}
